import { useEditorState, type Editor } from '@tiptap/react';
import type { ReactNode } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Code,
  Highlighter,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  PilcrowSquare,
  Quote,
  Redo2,
  Sparkles,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo2,
} from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import type { EditorKind } from './ClinicalEditorShell';
import { riskAssessmentTemplate, soapTemplate } from './editorTemplates';

interface Props {
  editor: Editor | null;
  kind: EditorKind;
}

interface IconBtnProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  ariaLabel: string;
  hint?: string;
  children: ReactNode;
}

function IconBtn({
  active = false,
  disabled = false,
  onClick,
  ariaLabel,
  hint,
  children,
}: IconBtnProps) {
  return (
    <button
      type="button"
      // Keep focus in the editor. A blur saves, and the first save of an AI draft
      // drops its banner, which shifts the toolbar before the click lands.
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
      title={hint !== undefined ? `${ariaLabel} · ${hint}` : ariaLabel}
      className={cn(
        'group relative inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors',
        'hover:text-accent-ink hover:bg-primary-50 text-neutral-600',
        active && 'bg-primary-50 text-accent-ink',
        disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden className="bg-accent-ink/10 mx-1.5 h-5 w-px shrink-0" />;
}

const HEADING_LABELS: { level: 0 | 1 | 2 | 3; label: string; sample: string }[] = [
  { level: 0, label: 'Paragraph', sample: 'Aa' },
  { level: 1, label: 'Heading 1', sample: 'H1' },
  { level: 2, label: 'Heading 2', sample: 'H2' },
  { level: 3, label: 'Heading 3', sample: 'H3' },
];

interface ToolbarState {
  activeHeadingLevel: 0 | 1 | 2 | 3;
  activeHeadingLabel: string;
  canUndo: boolean;
  canRedo: boolean;
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrike: boolean;
  isHighlight: boolean;
  isCode: boolean;
  isBulletList: boolean;
  isOrderedList: boolean;
  isTaskList: boolean;
  isQuote: boolean;
  isLink: boolean;
  isAlignLeft: boolean;
  isAlignCenter: boolean;
  isAlignRight: boolean;
}

function computeToolbarState(ed: Editor): ToolbarState {
  const activeHeadingLevel: 0 | 1 | 2 | 3 = ed.isActive('heading', { level: 1 })
    ? 1
    : ed.isActive('heading', { level: 2 })
      ? 2
      : ed.isActive('heading', { level: 3 })
        ? 3
        : 0;
  const activeHeadingLabel =
    HEADING_LABELS.find((h) => h.level === activeHeadingLevel)?.label ?? 'Paragraph';
  return {
    activeHeadingLevel,
    activeHeadingLabel,
    canUndo: ed.can().undo(),
    canRedo: ed.can().redo(),
    isBold: ed.isActive('bold'),
    isItalic: ed.isActive('italic'),
    isUnderline: ed.isActive('underline'),
    isStrike: ed.isActive('strike'),
    isHighlight: ed.isActive('highlight'),
    isCode: ed.isActive('code'),
    isBulletList: ed.isActive('bulletList'),
    isOrderedList: ed.isActive('orderedList'),
    isTaskList: ed.isActive('taskList'),
    isQuote: ed.isActive('blockquote'),
    isLink: ed.isActive('link'),
    isAlignLeft: ed.isActive({ textAlign: 'left' }),
    isAlignCenter: ed.isActive({ textAlign: 'center' }),
    isAlignRight: ed.isActive({ textAlign: 'right' }),
  };
}

export function EditorToolbar({ editor, kind }: Props) {
  // useEditorState is the official Tiptap subscription primitive — it reads
  // the editor on transactions but only re-renders when the *selected* values
  // change. Avoids the infinite-loop issues a manual `editor.on('transaction')`
  // + force-setState pattern produces when nested Radix dropdowns
  // (block-style, insert) open inside the toolbar.
  const subscribed = useEditorState({
    editor,
    selector: ({ editor: ed }): ToolbarState | null => {
      if (ed === null) return null;
      return computeToolbarState(ed);
    },
  });

  if (editor === null) {
    return (
      <div aria-hidden className="border-accent-ink/10 sticky top-15 z-9 h-11 border-b bg-white" />
    );
  }

  // `subscribed` is `null` for a brief moment after editor creation but before
  // useEditorState's first selector run lands. Compute live state synchronously
  // as a fallback so the toolbar is fully populated from the very first frame
  // — prevents the empty-bar flash the user reported on cold load.
  const state: ToolbarState = subscribed ?? computeToolbarState(editor);

  const setHeading = (level: 0 | 1 | 2 | 3): void => {
    if (level === 0) {
      editor.chain().focus().setParagraph().run();
      return;
    }
    editor.chain().focus().toggleHeading({ level }).run();
  };

  const promptLink = (): void => {
    const previousUrl = (editor.getAttributes('link').href as string | undefined) ?? '';
    const url = window.prompt('Link URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div
      role="toolbar"
      aria-label="Document formatting"
      // Sticky directly under the topbar (60px). Solid white surface with a
      // single brand-tinted hairline border-b — no blur, no shadow, since the
      // card shell already owns the elevation. z-9 stays under the topbar's
      // z-10 so the topbar's border-b always wins at the seam.
      className="border-accent-ink/10 sticky top-15 z-9 flex h-11 items-center gap-0.5 border-b bg-white px-4"
    >
      <IconBtn
        ariaLabel="Undo"
        hint="⌘Z"
        disabled={!state.canUndo}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Redo"
        hint="⌘⇧Z"
        disabled={!state.canRedo}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <Divider />

      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors',
            'text-accent-ink hover:bg-primary-50',
          )}
          aria-label="Block style"
        >
          <span className="font-display italic">{state.activeHeadingLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.5} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuLabel className="font-display tracking-eyebrow text-accent-ink px-2 text-[10px] font-medium uppercase italic">
            Block
          </DropdownMenuLabel>
          {HEADING_LABELS.map((h) => {
            const isActive = state.activeHeadingLevel === h.level;
            return (
              <DropdownMenuItem
                key={h.level}
                onClick={() => {
                  setHeading(h.level);
                }}
                className="flex items-center justify-between"
              >
                <span className={cn('flex items-center gap-2', isActive && 'text-accent-ink')}>
                  <span className="font-display text-accent-ink/70 inline-block w-7 italic">
                    {h.sample}
                  </span>
                  {h.label}
                </span>
                {isActive ? (
                  <span aria-hidden className="bg-primary-400 h-1.5 w-1.5 rounded-full" />
                ) : null}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Divider />

      <IconBtn
        ariaLabel="Bold"
        hint="⌘B"
        active={state.isBold}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" strokeWidth={1.75} />
      </IconBtn>
      <IconBtn
        ariaLabel="Italic"
        hint="⌘I"
        active={state.isItalic}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Underline"
        hint="⌘U"
        active={state.isUnderline}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Strikethrough"
        active={state.isStrike}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Highlight"
        active={state.isHighlight}
        onClick={() => editor.chain().focus().toggleHighlight().run()}
      >
        <Highlighter className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Inline code"
        active={state.isCode}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <Divider />

      <IconBtn
        ariaLabel="Bullet list"
        active={state.isBulletList}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Numbered list"
        active={state.isOrderedList}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Task list"
        active={state.isTaskList}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <ListTodo className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <Divider />

      <IconBtn
        ariaLabel="Quote"
        active={state.isQuote}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn ariaLabel="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
        <Minus className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <Divider />

      <IconBtn
        ariaLabel="Align left"
        active={state.isAlignLeft}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        <AlignLeft className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Align center"
        active={state.isAlignCenter}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        <AlignCenter className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>
      <IconBtn
        ariaLabel="Align right"
        active={state.isAlignRight}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        <AlignRight className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <Divider />

      <IconBtn ariaLabel="Insert link" active={state.isLink} onClick={promptLink}>
        <LinkIcon className="h-4 w-4" strokeWidth={1.5} />
      </IconBtn>

      <span className="flex-1" />

      {kind === 'note' ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition-colors',
              'text-accent-ink hover:bg-primary-50',
            )}
            aria-label="Insert clinical scaffold"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="font-display italic">Insert</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={1.5} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            <DropdownMenuLabel className="font-display tracking-eyebrow text-accent-ink px-2 text-[10px] font-medium uppercase italic">
              Clinical scaffolds
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                editor.chain().focus().insertContent(soapTemplate).run();
              }}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <span className="font-medium">Insert SOAP scaffold</span>
              <span className="text-xs text-neutral-500">
                Subjective · Objective · Assessment · Plan
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                editor.chain().focus().insertContent(riskAssessmentTemplate).run();
              }}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <span className="font-medium">Insert risk-assessment scaffold</span>
              <span className="text-xs text-neutral-500">
                Risk factors · Protective · Level · Safety · Follow-up
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                editor.chain().focus().setHorizontalRule().run();
              }}
              className="flex items-center gap-2 py-1.5"
            >
              <PilcrowSquare className="h-4 w-4 opacity-60" />
              <span className="text-sm">Section break</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
